<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { useRoute } from 'vue-router'
import ProjectFilters from './ProjectFilters.vue'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Save } from 'lucide-vue-next'
import { Tabs } from '@/components/ui/tabs'
import { TabsList } from '@/components/ui/tabs'
import { TabsTrigger } from '@/components/ui/tabs'
import { TabsContent } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import LayerFeaturesList from '@/components/layers/variants/LayerFeaturesList.vue'

const route = useRoute()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const isUpdating = ref(false)
const currentFilters = ref(null)
const activeTab = ref("data")
const opacity = ref(100)
const map = computed(() => store.getters['map/getMap'])
const selectedRange = computed(() => store.state.layers.selectedRange)

const layer = computed(() => {
  return store.getters['layers/getOrgLayerById'](orgId.value, layerId.value)
})

const isCollectedDataLayer = computed(() => {
  return layer.value?.properties?.type === 4
})

const layerCode = computed(() => {
  return layer.value?.uuid
})

const settings = computed(() => store.getters['user/getUserSettings'])

const getActiveTabKey = computed(() => {
  return `layer_${layerId.value}_active_tab`
})

const fetchLayerData = async () => {
  if (!orgId.value || !layerId.value) return

  try {
    const response = await API.get(`${HOST}/layers/${layerId.value}`)
    const layerData = response.data

    if (layerData) {
      await store.dispatch('layers/updateOrgLayer', {
        orgId: orgId.value,
        layer: {
          ...layer.value,
          properties: {
            ...layer.value?.properties,
            layer: layerData.layer
          }
        }
      })
    }
  } catch (error) {
    console.error('Error fetching layer data:', error)
    toast({
      title: 'Error',
      description: 'Failed to load layer data',
      variant: 'destructive'
    })
  }
}

const fetchFeatures = async () => {
  if (!layerCode.value) return

  try {
    const [startDate, endDate] = selectedRange.value || [];

    let url = `${HOST}/layers/${layerCode.value}/features`;
    if (startDate && endDate) {
      url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }

    const response = await API({
      method: 'GET',
      url: url
    });

    if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
      const currentLayer = store.getters['layers/getOrgLayerById'](orgId.value, layerId.value);

      if (currentLayer) {
        const updatedLayer = {
          ...currentLayer,
          properties: {
            ...currentLayer.properties,
            features: response.data.features,
            featureCollection: {
              type: 'FeatureCollection',
              features: response.data.features
            }
          }
        };

        await store.dispatch('layers/updateOrgLayer', {
          orgId: orgId.value,
          layer: updatedLayer
        });
      }
    }
  } catch (error) {
    console.error('Error loading features:', error);
    toast({
      title: 'Error loading features',
      description: 'Failed to fetch features after filter update',
      variant: 'destructive'
    });
  }
}

const fetchDates = async () => {
  if (!layerCode.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/layers/${layerCode.value}/radar/dates`
    });

    if (response.data) {
      let processedDates = [];

      if (response.data.layers && Array.isArray(response.data.layers)) {
        const templateLayers = response.data.layers.find(layer => layer.id === 'features' && layer.layers);

        if (templateLayers && templateLayers.layers) {
          processedDates = templateLayers.layers.map(layer => ({
            id: layer.id || 'unknown',
            name: layer.name || 'Unknown Template',
            color: layer.color || 'blue',
            dates: Array.isArray(layer.dates) ? layer.dates : []
          }));
        } else {
          processedDates = response.data.layers.map(layer => ({
            id: layer.id || 'unknown',
            name: layer.name || (layer.id === 'features' ? 'Features' : layer.id),
            color: layer.color || 'blue',
            dates: Array.isArray(layer.dates) ? layer.dates : []
          }));
        }
      } else if (response.data.dates) {
        processedDates = [{
          id: "features",
          name: "Features",
          color: "green",
          dates: Array.isArray(response.data.dates) ? response.data.dates : []
        }];
      }

      if (processedDates.length > 0) {
        store.commit('layers/ADD_DATES', processedDates);
      }
    }
  } catch (error) {
    console.error('Error fetching dates:', error);
    toast({
      title: 'Error loading dates',
      description: 'Failed to fetch dates after filter update',
      variant: 'destructive'
    });
  }
}

const saveFilterChanges = async (filterData) => {
  if (!layer.value || layer.value.properties.type !== 4) return

  isUpdating.value = true
  try {
    const filterConfig = {
      type: "AndFilter",
      config: []
    };

    if (filterData.projectIds && filterData.projectIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "project_id",
        config: filterData.projectIds.map(id => id.toString())
      });
    }

    if (filterData.templateIds && filterData.templateIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "template_id",
        config: filterData.templateIds.map(id => id.toString())
      });
    }

    if (filterData.memberIds && filterData.memberIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "member_id",
        config: filterData.memberIds.map(id => id.toString())
      });
    }

    if (filterData.searchText) {
      filterConfig.config.push({
        type: "TextFilter",
        field_name: "search",
        config: filterData.searchText
      });
    }

    if (filterData.isPublicData !== undefined) {
      filterConfig.config.push({
        type: "BoolFilter",
        field_name: "is_public",
        config: filterData.isPublicData
      });
    }

    if (filterData.dateRange && filterData.dateRange.start && filterData.dateRange.end) {
      filterConfig.config.push({
        type: "DateRangeFilter",
        field_name: "acquired",
        config: {
          gte: filterData.dateRange.start.toISOString(),
          lte: filterData.dateRange.end.toISOString()
        }
      });
    }

    if (filterData.geometry) {
      filterConfig.config.push({
        type: "GeometryFilter",
        field_name: "geometry",
        config: filterData.geometry.geometry
      });
    }

    if (filterData.geometryTypes && filterData.geometryTypes.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "geometry_type",
        config: filterData.geometryTypes
      });
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/layers/${layerId.value}/collected-data`,
      data: {
        filter: filterConfig
      }
    })

    const updatedLayer = {
      ...layer.value,
      properties: {
        ...layer.value.properties,
        layer: {
          ...layer.value.properties.layer,
          data: {
            ...layer.value.properties.layer?.data,
            filter: filterConfig
          }
        }
      }
    }

    await store.dispatch('layers/updateOrgLayer', {
      orgId: orgId.value,
      layer: updatedLayer
    })

    await fetchFeatures()
    await fetchDates()
  } catch (error) {
    console.error('Error saving filters:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to save filter settings',
      variant: 'destructive'
    });
  } finally {
    isUpdating.value = false;
  }
}

const updateFilters = (filters) => {
  currentFilters.value = filters
}

const onOpacityChange = (value) => {
  opacity.value = value[0]

  if (!map.value || !layerId.value) return

  try {
    const layers = map.value.getStyle().layers.filter((layer) =>
      layer.metadata?.groupId === layerId.value
    )

    layers.forEach((layer) => {
      if (layer.type === 'fill') {
        map.value.setPaintProperty(
          layer.id,
          'fill-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'line') {
        map.value.setPaintProperty(
          layer.id,
          'line-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'circle') {
        map.value.setPaintProperty(
          layer.id,
          'circle-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'symbol') {
        map.value.setPaintProperty(
          layer.id,
          'text-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'raster') {
        map.value.setPaintProperty(
          layer.id,
          'raster-opacity',
          opacity.value / 100
        )
      }
    })

    const updatedLayer = {
      ...layer.value,
      properties: {
        ...layer.value.properties,
        opacity: opacity.value / 100
      }
    }

    store.dispatch('layers/updateOrgLayer', {
      orgId: orgId.value,
      layer: updatedLayer
    })
  } catch (error) {
    console.error('Error updating layer opacity:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to update layer opacity',
      variant: 'destructive'
    })
  }
}

watch(activeTab, (newTab) => {
  if (layerId.value) {
    localStorage.setItem(getActiveTabKey.value, newTab)
  }
})

onMounted(() => {
  fetchLayerData()

  if (layer.value?.properties?.opacity !== undefined) {
    opacity.value = Math.round(layer.value.properties.opacity * 100)
  }

  if (layerId.value) {
    const savedTab = localStorage.getItem(getActiveTabKey.value)
    if (savedTab) {
      activeTab.value = savedTab
    }
  }
})

watch([layerId, orgId], fetchLayerData)
</script>

<template>
  <div class="p-3" v-if="layer">
    <div class="flex justify-between items-center">
      <h3 class="text-lg font-bold">{{ layer.properties.name }}</h3>
      <Button
        v-if="layer?.properties?.type === 4 && activeTab === 'filter'"
        @click="saveFilterChanges(currentFilters)"
        :disabled="isUpdating"
        size="sm"
        class="bg-primary text-white hover:bg-primary/90"
      >
        <Save class="h-4 w-4 mr-2" />
        <span v-if="isUpdating">Saving...</span>
        <span v-else>Save</span>
      </Button>
    </div>
    <ul class="text-gray-500">
      <li><span>Created at: </span> {{ formatTime(layer?.properties?.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
      <li v-if="layer?.properties?.updated_at !== layer?.properties?.created_at">Updated at: {{ formatTime(layer?.properties?.updated_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
    </ul>

    <div class="mt-4">
      <Tabs v-model="activeTab" default-value="data" class="w-full">
        <TabsList class="grid grid-cols-3">
          <TabsTrigger value="data">Data</TabsTrigger>
          <TabsTrigger value="filter">Filter</TabsTrigger>
          <TabsTrigger value="style">Style</TabsTrigger>
        </TabsList>

        <div class="mt-4">
          <TabsContent value="data">
            <div v-if="isCollectedDataLayer">
              <LayerFeaturesList :code="layerCode" />
            </div>
            <p v-else class="text-sm text-muted-foreground">No additional data available for this layer</p>
          </TabsContent>

          <TabsContent value="style">
            <div class="flex items-center justify-between space-x-4">
              <h4 class="text-sm font-medium">Opacity</h4>
              <Slider
                :default-value="[opacity]"
                :max="100"
                :step="1"
                class="w-full max-w-md"
                @update:model-value="onOpacityChange($event)"
              />
              <span class="text-sm">
                {{ opacity }}%
              </span>
            </div>
          </TabsContent>

          <TabsContent value="filter">
            <ProjectFilters
              v-if="layer?.properties?.layer?.data && layer.properties.type === 4"
              :initial-filter="layer?.properties?.layer?.data"
              @update:filters="updateFilters"
            />
            <p v-else class="text-sm text-muted-foreground">This layer doesn't support filters</p>
          </TabsContent>
        </div>

      </Tabs>
    </div>
  </div>
</template>
